import React from 'react';
import GlassButton from '../../components/Aero/GlassButton';
import AeroIcon from '../../components/Aero/AeroIcon';
import AeroPageHeader from '../../components/Aero/AeroPageHeader';
import AeroDotLoader from '../../components/Aero/AeroDotLoader';
import AeroStatePanel from '../../components/Aero/AeroStatePanel';
import {
  AeroTimeline,
  AeroTimelineDay,
  AeroEventRow,
  AeroSiteBlock,
  AeroTestLine,
  AeroEmptyPanel,
  AeroWayRow,
} from '../../components/Aero/AeroHomeParts';
import { timeLabel } from './homeModel';
import './AeroHome.css';

/**
 * AeroHomeView: Home, layout A (decided 23 Oct 2026), as a pure view. What's
 * new comes first (a timeline of events, newest first); sites sit beside it as
 * preview blocks and tests as status lines. With nothing yet, the first-run
 * panel teaches Publish, Test, Review and offers the ways in.
 * AeroHome feeds it real data; /aero feeds it Example data.
 *
 * Props:
 *   state        'loading' | 'ready' | 'empty' | 'error'
 *   groups       [{ key, label, date, items: [event] }] (homeModel.groupByDay)
 *   sites        [{ id, name, address, status, favicon, example }]; siteCount
 *   tests        [{ id, name, meta, dots, status, example }]; testCount
 *   isFree       shows the Free plan note on the first-run panel
 *   onNewSite(source), onNewTest(), onOpen(path), onRetry()
 *   errorKind    with state 'error': 'offline' | 'error' (AeroStatePanel kind)
 */
const WAYS = [
  { icon: 'up', title: 'Upload HTML or a zip', desc: 'A folder or zip with index.html at the top. Live in seconds.', source: null },
  { icon: 'prompt', title: 'A prompt for Claude or ChatGPT', desc: 'Built it in a chat? Copy one prompt into that chat and it publishes here.', source: 'agent' },
  { icon: 'figma', title: 'A Figma prototype link', desc: 'Paste the share link. Flows and transitions work as in Figma.', source: null },
];

const STEPS = [
  ['01', 'Publish', 'Your site appears here with its address.'],
  ['02', 'Test', 'Send one link. Each session shows up here when it ends.'],
  ['03', 'Review', 'A transcript and a summary, in their own words.'],
];

function Column({ title, count, linkLabel, onLink, children }) {
  return (
    <section className="aero-home-col">
      <div className="aero-home-hh">
        <h2>{title}</h2>
        {typeof count === 'number' && <span className="aero-home-ct">{count}</span>}
        {onLink && (
          <button type="button" className="aero-home-lnk" onClick={onLink}>
            {linkLabel}
            <AeroIcon name="arrow" />
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

export default function AeroHomeView({
  state = 'ready',
  groups = [],
  sites = [],
  siteCount = null,
  tests = [],
  testCount = null,
  isFree = false,
  onNewSite = () => {},
  onNewTest = () => {},
  onOpen = () => {},
  onRetry,
  errorKind = 'error',
}) {
  const header = (
    <AeroPageHeader
      crumbs="Home"
      actions={(
        <>
          <GlassButton fill="glass" size="sm" onClick={onNewTest}>
            <AeroIcon name="plus" />
            New test
          </GlassButton>
          <GlassButton fill="ink" size="sm" onClick={() => onNewSite(null)}>
            <AeroIcon name="plus" />
            New site
          </GlassButton>
        </>
      )}
    />
  );

  if (state === 'empty') {
    return (
      <div className="aero-home">
        {header}
        <div className="aero-home-body">
          <AeroEmptyPanel
            wash="hero"
            title="Put something online"
            lead="Then share one link and watch people use it. Start from what you have."
          >
            <div className="aero-ways">
              {WAYS.map((w) => (
                <AeroWayRow key={w.title} icon={w.icon} title={w.title} desc={w.desc} onClick={() => onNewSite(w.source)} />
              ))}
            </div>
            <div className="aero-steps">
              {STEPS.map(([n, t, d]) => (
                <div key={n} className="aero-step">
                  <span>{n}</span>
                  <b>{t}</b>
                  <p>{d}</p>
                </div>
              ))}
            </div>
            {isFree && (
              <div className="aero-plannote">Free plan: 3 test sessions, and sites stay live for 7 days.</div>
            )}
          </AeroEmptyPanel>
        </div>
      </div>
    );
  }

  // Nothing could be read (offline, or the read failed): one plain state with
  // Try again, never the first-run panel or empty columns.
  if (state === 'error') {
    return (
      <div className="aero-home">
        {header}
        <AeroStatePanel
          kind={errorKind === 'offline' ? 'offline' : 'error'}
          title={errorKind === 'offline' ? undefined : "Home didn't load"}
          actions={onRetry ? <GlassButton fill="ink" size="sm" onClick={onRetry}>Try again</GlassButton> : null}
        />
      </div>
    );
  }

  let timeline;
  if (state === 'loading') {
    timeline = <div className="aero-home-wait"><AeroDotLoader size="md" label="Loading" /></div>;
  } else if (!groups.length) {
    timeline = <div className="aero-home-quiet">Nothing new yet.</div>;
  } else {
    timeline = (
      <AeroTimeline>
        {groups.map((g) => (
          <React.Fragment key={g.key}>
            <AeroTimelineDay label={g.label} date={g.date} />
            {g.items.map((e) => (
              <AeroEventRow
                key={e.key}
                time={timeLabel(e.at)}
                kind={e.kind}
                subject={e.subject}
                who={e.who}
                onOpen={e.to ? () => onOpen(e.to) : undefined}
                action={e.to && e.action ? (
                  <GlassButton fill="glass" size="xs" onClick={() => onOpen(e.to)} tabIndex={-1}>
                    {e.action}
                  </GlassButton>
                ) : null}
              >
                {e.text}
                {e.code && <> <code>{e.code}</code>.</>}
              </AeroEventRow>
            ))}
          </React.Fragment>
        ))}
      </AeroTimeline>
    );
  }

  return (
    <div className="aero-home">
      {header}
      <div className="aero-home-body aero-home-a">
        <section className="aero-home-new">
          <div className="aero-home-hh">
            <h2>What&rsquo;s new</h2>
          </div>
          {timeline}
        </section>
        <div className="aero-home-side">
          <Column title="Sites" count={siteCount} linkLabel="All sites" onLink={() => onOpen('/sites')}>
            {state === 'loading' ? null : sites.length ? (
              <div className="aero-home-sites">
                {sites.map((s) => (
                  <AeroSiteBlock
                    key={s.id}
                    name={s.name}
                    address={s.address}
                    status={s.status}
                    favicon={s.favicon}
                    previewUrl={s.previewUrl}
                    previewColors={s.previewColors}
                    example={s.example}
                    onOpen={() => onOpen(`/sites/${s.id}`)}
                  />
                ))}
              </div>
            ) : (
              <button type="button" className="aero-home-add" onClick={() => onNewSite(null)}>
                <AeroIcon name="plus" />
                Put a site online
              </button>
            )}
          </Column>
          <Column title="Tests" count={testCount} linkLabel="All tests" onLink={() => onOpen('/user-testing')}>
            {state === 'loading' ? null : tests.length ? (
              <div className="aero-tlist">
                {tests.map((t) => (
                  <AeroTestLine
                    key={t.id}
                    name={t.name}
                    meta={t.meta}
                    dots={t.dots}
                    status={t.status}
                    previewUrl={t.previewUrl}
                    previewColors={t.previewColors}
                    example={t.example}
                    newCount={t.newCount || 0}
                    onOpen={() => onOpen(t.newCount ? `/user-testing/${t.id}/sessions` : `/user-testing/${t.id}`)}
                  />
                ))}
              </div>
            ) : (
              <button type="button" className="aero-home-add" onClick={onNewTest}>
                <AeroIcon name="plus" />
                Make a test
              </button>
            )}
          </Column>
        </div>
      </div>
    </div>
  );
}
